import {
  CaretDownFilled,
  CaretUpFilled,
  FilterOutlined,
} from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";
import { Spiner } from "@components/loaders/spiner";
import { HomeService } from "@services/home.service";
import { usePageHeader } from "@hooks/use_page_header";
import { useQuery } from "@tanstack/react-query";
import { useStudentBio } from "@hooks/use_student_bio";
import { toRp } from "@utils/helper";
import { Button, Select } from "antd";
import moment from "moment-timezone";
import { useState } from "react";
import { LuInbox } from "react-icons/lu";

moment.locale("id");

export const CheckingAccDataPage = () => {
  usePageHeader("Laporan Uang Saku", { bottomNavigation: "hidden" });
  const [limit, setLimit] = useState<string>("5");
  const [popupFilter, setPopupFilter] = useState<boolean>(false);
  const [openDetails, setOpenDetails] = useState<number[]>([]);

  const toggleDetail = (idx: number) => {
    setOpenDetails((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
  };

  const { nis } = useStudentBio();

  const { data: laporanUangSaku, isPending: isPendingLaporanUangSaku } =
    useQuery({
      queryKey: [HomeService.queries.GET_DATA_REPORT_BALANCES, nis, limit],
      queryFn: () => HomeService.reportBalances({ nis, limit }),
    });


  return (
    <>
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        <>
          {/* filter bar */}
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <div className="tw-text-sm tw-text-text-secondary">
              Menampilkan{" "}
              <span className="tw-font-semibold tw-text-text-primary">{limit} data terakhir</span>
            </div>
            <FilterOutlined
              className="tw-p-2 tw-text-xl tw-bg-transparent card-body"
              onClick={() => setPopupFilter(true)}
            />
          </div>

          {/* report */}
          <div>
            {isPendingLaporanUangSaku && (
              <div className="tw-text-center card-body">
                <Spiner className="" />
              </div>
            )}
            {laporanUangSaku?.data?.length === 0 ? (
              <div className="tw-p-8 tw-text-center card-body">
                <LuInbox className="tw-mx-auto tw-mb-2 tw-text-5xl tw-text-text-secondary tw-opacity-40" />
                <div className="tw-text-sm tw-text-text-secondary">Data tidak ditemukan</div>
              </div>
            ) : (
              (laporanUangSaku?.data ?? [])
                // .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
                // .filter((i) => i.tipe === "keluar")
                // .slice(0, parseInt(limit))
                .sort((a, b) => {
                  // keluar first
                  if (a.tipe === "keluar" && b.tipe !== "keluar") return -1;
                  if (a.tipe !== "keluar" && b.tipe === "keluar") return 1;

                  // if same tipe → sort by date desc
                  return moment(b.tanggal).diff(moment(a.tanggal));
                })
                .map((item, idx) => (
                  <div className="tw-mb-4 card-body" key={idx}>
                    <div className="tw-mb-1 tw-flex tw-justify-between tw-items-center tw-font-semibold">
                      <div className="tw-text-xs tw-font-medium tw-text-text-secondary">
                        {moment(item.tanggal ?? Date.now()).format(
                          "DD MMMM YYYY",
                        )}
                      </div>
                      <div className="tw-w-1/3 tw-flex tw-justify-end">
                        {item.tipe === "masuk" ? (
                          <span className="tw-px-2 tw-text-[10px] tw-inline-flex tw-items-center tw-rounded-md tw-bg-blue-50 tw-py-1 tw-font-medium tw-text-blue-700 tw-ring-1 tw-ring-blue-600/10 tw-ring-inset">
                            masuk
                          </span>
                        ) : (
                          <span className="tw-px-2 tw-text-[10px] tw-inline-flex tw-items-center tw-rounded-md tw-bg-red-50 tw-py-1 tw-font-medium tw-text-red-700 tw-ring-1 tw-ring-red-600/10 tw-ring-inset">
                            keluar
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="tw-flex tw-justify-between tw-items-end tw-font-semibold">
                      <div className="tw-text-sm tw-w-[60%] tw-text-text-primary">
                        {item.catatan}
                      </div>
                      <div className="tw-w-[40%] tw-text-end">
                        <div className="tw-text-base tw-text-text-primary">
                          {item.tipe === "masuk"
                            ? toRp(
                                parseFloat(
                                  ((item.uangsakumasuk ?? "") + "").replace(
                                    ",",
                                    "",
                                  ) ?? 0,
                                ),
                              )
                            : toRp(
                                parseFloat(
                                  ((item.uangkeluar ?? "") + "").replace(
                                    ",",
                                    "",
                                  ) ?? 0,
                                ),
                              )}
                        </div>
                      </div>
                    </div>
                    {item.detail &&
                      item.detail.length > 0 &&
                      item.jenis !== "" && (
                        <>
                          <div
                            className="tw-mt-2 tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-gap-1 tw-text-xs tw-text-text-secondary"
                            onClick={() => toggleDetail(idx)}
                          >
                            <span>Detail ({item.detail.length})</span>
                            <span>
                              {openDetails.includes(idx) ? (
                                <CaretUpFilled />
                              ) : (
                                <CaretDownFilled />
                              )}
                            </span>
                          </div>
                          {openDetails.includes(idx) && (
                            <div className="tw-mt-2 tw-border-t tw-pt-2 tw-space-y-2">
                              {item.detail.map((d, dIdx) => (
                                <div
                                  key={dIdx}
                                  className="tw-flex tw-justify-between tw-text-xs tw-text-text-secondary"
                                >
                                  {item.jenis.toLowerCase() === "ziswaf" ? (
                                    <>
                                      <span className="tw-w-1/2">
                                        {d.keterangan}
                                      </span>
                                      <span>
                                        {toRp(
                                          parseFloat(
                                            (d.nominal ?? "0").replace(",", ""),
                                          ),
                                        )}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="tw-w-1/2">
                                        {d.tagihan}
                                      </span>
                                      <span>
                                        {toRp(
                                          parseFloat(
                                            (d.dibayar ?? "0").replace(",", ""),
                                          ),
                                        )}
                                      </span>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                  </div>
                ))
            )}
          </div>
          {/* end of report */}
        </>
      </div>

      <PopupCustom
        title="Filter Data"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <div className="">
          <Select
            className=" tw-text-base"
            placeholder="Pilih Jumlah Ditampilkan"
            defaultValue={limit}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setLimit(e)}
            options={[
              { value: "5", label: "5 data terakhir" },
              { value: "10", label: "10 data terakhir" },
              { value: "25", label: "25 data terakhir" },
            ]}
          ></Select>
          <Button
            onClick={() => setPopupFilter(false)}
            className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-sm tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
            style={{ background: "#ef4444" }}
          >
            Tutup
          </Button>
        </div>
      </PopupCustom>
    </>
  );
};
